import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KImage, Rect, Text, Label, Tag, Group, Transformer } from 'react-konva';
import type { Region, Rect as RectT } from '@simultrain/shared';
import type Konva from 'konva';

type Props = {
  imageUrl: string | null;
  regions: Region[];
  selectedId: string | null;
  drawingKind: Region['kind'] | null;
  onSelect: (id: string | null) => void;
  onCreate: (rect: RectT) => void;
  onUpdateRect: (id: string, rect: RectT) => void;
};

const COLORS: Record<Region['kind'], string> = {
  'click-correct': '#22c55e',
  'click-error': '#ef4444',
  'input': '#38bdf8',
  'drag-source': '#a855f7',
  'text': '#f59e0b',
};

// A visible hint on the canvas itself for anything other than a plain left
// click — otherwise a double/right-click region looks identical to a normal
// one until you open its detail panel.
function clickBadge(region: Region): string | null {
  if (region.kind !== 'click-correct' && region.kind !== 'click-error') return null;
  const clickType = region.clickType ?? 'left';
  if (clickType === 'double') return '2×';
  if (clickType === 'right') return 'R';
  if (clickType === 'hover') return 'H';
  return null;
}

// TEMPORARY diagnostic aid (see the "resize/move sometimes doesn't respond"
// reports) — describes whatever currently holds keyboard focus. A likely
// culprit: after closing a native control like the <input type="color">
// swatch, the OS sometimes delivers the *next* click to the browser only as
// a window-refocus, not as a real click to the page — the very first
// press-and-drag right after using one would then silently do nothing.
function describeActiveElement(): string {
  const el = document.activeElement;
  if (!el || el === document.body) return 'body (niets)';
  const tag = el.tagName.toLowerCase();
  const type = el instanceof HTMLInputElement ? `[type=${el.type}]` : '';
  const cls = typeof el.className === 'string' && el.className ? '.' + el.className.split(' ')[0] : '';
  return `${tag}${type}${cls}`;
}

// Memoized so that editing an unrelated field (which replaces the `regions`
// array but keeps every *other* region object reference-equal) doesn't
// reapply this rect's props to Konva mid-gesture — that fight between a live
// drag/resize and a React-driven prop reset is what made resizing feel flaky.
const RegionRect = memo(function RegionRect({
  region,
  selected,
  offsetX,
  offsetY,
  drawW,
  drawH,
  onUpdateRect,
  logDebug,
}: {
  region: Region;
  selected: boolean;
  offsetX: number;
  offsetY: number;
  drawW: number;
  drawH: number;
  onUpdateRect: (id: string, rect: RectT) => void;
  logDebug: (msg: string) => void;
}) {
  const rectRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);
  // Snapshot taken at gesture START (onDragStart/onTransformStart) — by the
  // time onDragEnd/onTransformEnd fire, Konva has already moved the node
  // live, so reading "before" at commit time would just equal "after".
  const beforeRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  useEffect(() => {
    if (selected && trRef.current && rectRef.current) {
      trRef.current.nodes([rectRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [selected]);

  function snapshotBefore() {
    const node = rectRef.current;
    if (node) beforeRef.current = { x: node.x(), y: node.y(), w: node.width(), h: node.height() };
  }

  function commitRect(kind: 'sleep' | 'resize') {
    const node = rectRef.current;
    if (!node) return;
    const before = beforeRef.current ?? { x: node.x(), y: node.y(), w: node.width(), h: node.height() };
    // After drag or transform the node has applied scaleX/scaleY; flatten them.
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    const nx = (node.x() - offsetX) / drawW;
    const ny = (node.y() - offsetY) / drawH;
    const nw = (node.width() * scaleX) / drawW;
    const nh = (node.height() * scaleY) / drawH;
    onUpdateRect(region.id, {
      x: Math.max(0, Math.min(1, nx)),
      y: Math.max(0, Math.min(1, ny)),
      w: Math.max(0.005, Math.min(1 - nx, nw)),
      h: Math.max(0.005, Math.min(1 - ny, nh)),
    });
    logDebug(
      `✓ ${kind} voltooid op "${region.label || region.kind}": ` +
        `x ${before.x.toFixed(0)}→${node.x().toFixed(0)}, y ${before.y.toFixed(0)}→${node.y().toFixed(0)}, ` +
        `w ${before.w.toFixed(0)}→${(node.width() * scaleX).toFixed(0)}, h ${before.h.toFixed(0)}→${(node.height() * scaleY).toFixed(0)}`,
    );
  }

  return (
    <Group>
      <Rect
        ref={rectRef}
        x={offsetX + region.rect.x * drawW}
        y={offsetY + region.rect.y * drawH}
        width={region.rect.w * drawW}
        height={region.rect.h * drawH}
        // A text region previews its own actual (opaque) colors, since that's
        // exactly what it'll look like in the Player — every other kind uses
        // a translucent tint of a fixed per-kind color so it doesn't obscure
        // the screenshot underneath it.
        stroke={region.kind === 'text' ? region.borderColor : COLORS[region.kind]}
        strokeWidth={selected ? 2 : 1.5}
        fill={region.kind === 'text' ? region.backgroundColor : COLORS[region.kind] + (selected ? '40' : '20')}
        shadowColor="black"
        shadowBlur={12}
        shadowOffset={{ x: 0, y: 4 }}
        shadowOpacity={region.kind === 'text' && region.shadow ? 0.35 : 0}
        // Always draggable (not just once selected) — otherwise clicking an
        // unselected region and dragging in one motion only selects it,
        // since Konva reads `draggable` at gesture-start, before that click's
        // own selection state change has taken effect. A plain click still
        // just selects; only an actual drag moves it.
        draggable
        // No onMouseDown/onClick here for selection — that's handled once,
        // geometrically, by the Stage's own handler (see handleMouseDown
        // below). Relying on each Rect's own Konva hit-test proved
        // unreliable in practice; a single direct hit-test against the
        // actual rect data can't go stale the way Konva's per-shape hit
        // graph apparently can.
        onDragStart={() => { snapshotBefore(); logDebug(`↕ sleep gestart op "${region.label || region.kind}" — focus was: ${describeActiveElement()}`); }}
        onDragEnd={() => commitRect('sleep')}
        onTransformStart={() => { snapshotBefore(); logDebug(`⤡ formaat aanpassen gestart op "${region.label || region.kind}" — focus was: ${describeActiveElement()}`); }}
        onTransformEnd={() => commitRect('resize')}
      />
      {region.kind === 'text' && region.text ? (
        <Text
          x={offsetX + region.rect.x * drawW}
          y={offsetY + region.rect.y * drawH}
          width={region.rect.w * drawW}
          height={region.rect.h * drawH}
          text={region.text}
          fontSize={region.fontSize}
          fill={region.textColor}
          padding={8}
          wrap="word"
          listening={false}
        />
      ) : null}
      {!selected && (region.label || clickBadge(region)) ? (
        // Hidden while selected — the label pill otherwise sits right on top
        // of the selection handles, making it hard to see exactly where the
        // region's edges are while positioning/resizing it.
        // A dark background pill (rather than plain white text) keeps the
        // label readable regardless of what's underneath it in the screenshot.
        <Label
          x={offsetX + region.rect.x * drawW + 4}
          y={offsetY + region.rect.y * drawH + 4}
          listening={false}
        >
          <Tag fill="rgba(15, 23, 42, 0.85)" cornerRadius={3} />
          <Text
            text={[region.label, clickBadge(region)].filter(Boolean).join(' · ')}
            fill="white"
            fontSize={12}
            padding={3}
          />
        </Label>
      ) : null}
      {selected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          keepRatio={false}
          borderStroke={COLORS[region.kind]}
          anchorStroke={COLORS[region.kind]}
          anchorFill="white"
          anchorSize={14}
          anchorCornerRadius={3}
          // Extend each anchor's own hit region past its visual size (Konva-
          // native, via hitStrokeWidth) so a slightly-off grab still lands on
          // the anchor. Capped relative to the region's own on-screen size —
          // an uncapped 16px swallows the ENTIRE body of a region that's only
          // ~13px across (several in this chapter are that small), leaving no
          // way to grab the body itself to move it.
          anchorStyleFunc={(anchor) => {
            const w = region.rect.w * drawW;
            const h = region.rect.h * drawH;
            anchor.hitStrokeWidth(Math.max(2, Math.min(16, w / 4, h / 4)));
          }}
          boundBoxFunc={(oldBox, newBox) => {
            // Prevent resizing smaller than 10px.
            if (newBox.width < 10 || newBox.height < 10) return oldBox;
            return newBox;
          }}
        />
      )}
    </Group>
  );
});

export function RegionCanvas({
  imageUrl,
  regions,
  selectedId,
  drawingKind,
  onSelect,
  onCreate,
  onUpdateRect,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 450 });
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [imgNatural, setImgNatural] = useState({ w: 1280, h: 720 });
  const [drawing, setDrawing] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  // Zoom in for pixel-precise placement — 100% shows the whole screenshot
  // (the existing contain-fit), scaled up to MAX_ZOOM from there. Pinch-zoom
  // on a trackpad arrives as a wheel event with ctrlKey set (a Chrome/Safari
  // convention), so this also picks that up for free; a plain two-finger
  // scroll pans instead.
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 8;
  const [zoom, setZoom] = useState(1);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });

  // Diagnostic log for the "resize/move sometimes doesn't respond" reports
  // — see the comment on describeActiveElement(). Off by default (most
  // people never need it); the toggle remembers its state per browser since
  // it's a personal debugging preference, not project data.
  // Stable identity (empty deps) so passing it into the memoized RegionRect
  // doesn't itself defeat that memoization.
  const [debugVisible, setDebugVisible] = useState(
    () => localStorage.getItem('simultrain-studio-debug-log') === '1',
  );
  useEffect(() => {
    localStorage.setItem('simultrain-studio-debug-log', debugVisible ? '1' : '0');
  }, [debugVisible]);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const debugLogRef = useRef<string[]>([]);
  const logDebug = useCallback((msg: string) => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`;
    debugLogRef.current = [...debugLogRef.current.slice(-13), `${time}  ${msg}`];
    setDebugLog(debugLogRef.current);
  }, []);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(() => {
      const r = wrapRef.current!.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!imageUrl) { setImg(null); return; }
    let active = true;
    let objUrl: string | null = null;
    void (async () => {
      const token =
        JSON.parse(localStorage.getItem('simultrain-studio-auth') ?? '{}')?.state?.accessToken;
      const r = await fetch(imageUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) return;
      const blob = await r.blob();
      objUrl = URL.createObjectURL(blob);
      const im = new Image();
      im.onload = () => {
        if (!active) return;
        setImg(im);
        setImgNatural({ w: im.naturalWidth, h: im.naturalHeight });
      };
      im.src = objUrl;
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [imageUrl]);

  // A fresh screenshot starts back at fit-to-view — staying zoomed into
  // whatever spot you were just looking at on a *different* step's image
  // would be disorienting.
  useEffect(() => {
    setZoom(1);
    setPanPos({ x: 0, y: 0 });
  }, [imageUrl]);

  const scale = Math.min(size.w / imgNatural.w, size.h / imgNatural.h);
  const drawW = imgNatural.w * scale;
  const drawH = imgNatural.h * scale;
  const offsetX = (size.w - drawW) / 2;
  const offsetY = (size.h - drawH) / 2;

  // Zoom while keeping `point` (in stage-local/unscaled coordinates, e.g.
  // straight from getPointerPosition()) visually fixed in place. Reads
  // zoom/panPos directly from render state rather than via functional
  // setState updaters — nesting a setPanPos call inside a setZoom updater
  // let React's dev-mode double-invocation of updaters apply the pan shift
  // twice per call, compounding into a wildly wrong position after a few zooms.
  const zoomAtPoint = useCallback((point: { x: number; y: number }, factor: number) => {
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * factor));
    if (newZoom <= MIN_ZOOM + 0.001) {
      setZoom(MIN_ZOOM);
      setPanPos({ x: 0, y: 0 });
      return;
    }
    const pointTo = { x: (point.x - panPos.x) / zoom, y: (point.y - panPos.y) / zoom };
    setZoom(newZoom);
    setPanPos({ x: point.x - pointTo.x * newZoom, y: point.y - pointTo.y * newZoom });
  }, [zoom, panPos]);

  function handleWheel(e: Konva.KonvaEventObject<WheelEvent>) {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (!stage) return;
    if (e.evt.ctrlKey || e.evt.metaKey) {
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      zoomAtPoint(pointer, e.evt.deltaY > 0 ? 1 / 1.08 : 1.08);
    } else {
      setPanPos((prev) => ({ x: prev.x - e.evt.deltaX, y: prev.y - e.evt.deltaY }));
    }
  }

  function stageToNorm(x: number, y: number) {
    return {
      x: Math.max(0, Math.min(1, (x - offsetX) / drawW)),
      y: Math.max(0, Math.min(1, (y - offsetY) / drawH)),
    };
  }

  // Arrow keys nudge the selected region by 1 screen px (10px with Shift);
  // Alt+arrow resizes it the same way instead of moving it — dragging by
  // hand for pixel precision is fiddly (especially on the smaller regions in
  // this chapter, where the resize handles and the body are only a few px
  // apart), this isn't. Skipped whenever a text field has focus so it
  // doesn't hijack normal cursor movement while editing a field elsewhere.
  useEffect(() => {
    const MOVE = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] } as const;
    const RESIZE = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowDown: [0, 1], ArrowUp: [0, -1] } as const;

    function onKeyDown(e: KeyboardEvent) {
      if (!selectedId || drawW === 0 || drawH === 0) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active as HTMLElement | null)?.isContentEditable) return;

      const region = regions.find((r) => r.id === selectedId);
      if (!region) return;
      const stepPx = e.shiftKey ? 10 : 1;
      // Zoomed-in screen pixels are smaller in logical/normalized terms —
      // 1 key press should still mean 1 screen px, so nudging gets *more*
      // precise the further you've zoomed in, not less.
      const drawWZoomed = drawW * zoom;
      const drawHZoomed = drawH * zoom;

      if (e.altKey) {
        const d = RESIZE[e.key as keyof typeof RESIZE];
        if (!d) return;
        e.preventDefault();
        const nw = Math.max(0.005, Math.min(1 - region.rect.x, region.rect.w + (d[0] * stepPx) / drawWZoomed));
        const nh = Math.max(0.005, Math.min(1 - region.rect.y, region.rect.h + (d[1] * stepPx) / drawHZoomed));
        onUpdateRect(selectedId, { ...region.rect, w: nw, h: nh });
        return;
      }

      const dir = MOVE[e.key as keyof typeof MOVE];
      if (!dir) return;
      e.preventDefault();
      const nx = Math.max(0, Math.min(1 - region.rect.w, region.rect.x + (dir[0] * stepPx) / drawWZoomed));
      const ny = Math.max(0, Math.min(1 - region.rect.h, region.rect.y + (dir[1] * stepPx) / drawHZoomed));
      onUpdateRect(selectedId, { ...region.rect, x: nx, y: ny });
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, regions, drawW, drawH, zoom, onUpdateRect]);

  function handleMouseDown(e: Konva.KonvaEventObject<MouseEvent>) {
    // Don't interfere with an active Transformer's own anchor/border drag
    // (resize) — that's Konva's own gesture, unrelated to selection. Checked
    // by class NAME rather than `instanceof Konva.Transformer`: react-konva
    // resolves its own copy of the konva package, so the anchor's prototype
    // chain doesn't necessarily share identity with our own import's class.
    if (e.target.getParent()?.getClassName() === 'Transformer') {
      logDebug(`⚓ mousedown op hoekpunt "${e.target.name()}" — focus was: ${describeActiveElement()}`);
      return;
    }

    const stage = e.target.getStage();
    // Relative (not plain) pointer position: inverts the stage's own
    // zoom/pan transform, so this always lands back in the same
    // unscaled/logical coordinate space as offsetX/drawW etc. below,
    // regardless of how far zoomed in or panned the view currently is.
    const pos = stage?.getRelativePointerPosition();
    if (!pos) {
      logDebug('⚠ mousedown zonder geldige pointerpositie (genegeerd)');
      return;
    }

    // A direct geometric hit-test against the actual region data, rather
    // than trusting Konva's own per-shape hit-test/event target. Selecting
    // via the latter (each Rect's own click/pointer handler) proved
    // unreliable in real use for reasons we couldn't pin down — this can't
    // go stale since it's computed fresh from the source data every time,
    // and .reverse() picks the top-most match for any genuinely overlapping regions.
    const nx = (pos.x - offsetX) / drawW;
    const ny = (pos.y - offsetY) / drawH;
    const hit = [...regions].reverse().find(
      (r) => nx >= r.rect.x && nx <= r.rect.x + r.rect.w && ny >= r.rect.y && ny <= r.rect.y + r.rect.h,
    );

    if (drawingKind && img) {
      if (!hit) setDrawing({ x: pos.x, y: pos.y, w: 0, h: 0 });
      logDebug(`mousedown (teken-modus) → ${hit ? `bestaand gebied "${hit.label || hit.kind}", genegeerd` : 'lege plek, tekenen gestart'}`);
      return;
    }
    logDebug(`● mousedown → ${hit ? `"${hit.label || hit.kind}"` : 'lege plek (deselecteert)'} — focus was: ${describeActiveElement()}`);
    onSelect(hit?.id ?? null);
  }

  function handleMouseMove(e: Konva.KonvaEventObject<MouseEvent>) {
    if (!drawing) return;
    const pos = e.target.getStage()?.getRelativePointerPosition();
    if (!pos) return;
    setDrawing({ ...drawing, w: pos.x - drawing.x, h: pos.y - drawing.y });
  }

  function handleMouseUp() {
    if (!drawing) return;
    const x1 = drawing.x, y1 = drawing.y;
    const x2 = drawing.x + drawing.w, y2 = drawing.y + drawing.h;
    const a = stageToNorm(Math.min(x1, x2), Math.min(y1, y2));
    const b = stageToNorm(Math.max(x1, x2), Math.max(y1, y2));
    const rect = { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    setDrawing(null);
    if (rect.w > 0.005 && rect.h > 0.005) onCreate(rect);
  }

  return (
    <div ref={wrapRef} className="canvas-wrap">
      <Stage
        width={size.w}
        height={size.h}
        scaleX={zoom}
        scaleY={zoom}
        x={panPos.x}
        y={panPos.y}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <Layer>
          {img && <KImage image={img} x={offsetX} y={offsetY} width={drawW} height={drawH} />}
          {regions.map((r) => (
            <RegionRect
              key={r.id}
              region={r}
              selected={r.id === selectedId}
              offsetX={offsetX}
              offsetY={offsetY}
              drawW={drawW}
              drawH={drawH}
              onUpdateRect={onUpdateRect}
              logDebug={logDebug}
            />
          ))}
          {drawing && (
            <Rect
              x={Math.min(drawing.x, drawing.x + drawing.w)}
              y={Math.min(drawing.y, drawing.y + drawing.h)}
              width={Math.abs(drawing.w)}
              height={Math.abs(drawing.h)}
              stroke={drawingKind ? COLORS[drawingKind] : 'white'}
              dash={[4, 4]}
              strokeWidth={2}
              listening={false}
            />
          )}
        </Layer>
      </Stage>
      <div className="canvas-zoom-controls" title="Ctrl/Cmd + scroll (of knijpen op een trackpad) om te zoomen, scroll om te pannen">
        {/* Left of − (not right of +) on purpose: it only appears once
            zoomed in, and putting it right next to + would land it under
            the cursor exactly where a second "zoom in some more" click
            would go, resetting the zoom instead. */}
        {zoom !== MIN_ZOOM && (
          <button type="button" onClick={() => { setZoom(MIN_ZOOM); setPanPos({ x: 0, y: 0 }); }}>Reset</button>
        )}
        <button type="button" onClick={() => zoomAtPoint({ x: size.w / 2, y: size.h / 2 }, 1 / 1.4)} disabled={zoom <= MIN_ZOOM}>−</button>
        <span>{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => zoomAtPoint({ x: size.w / 2, y: size.h / 2 }, 1.4)} disabled={zoom >= MAX_ZOOM}>+</button>
      </div>
      {debugVisible ? (
        // Plain text so it can be selected and pasted into a bug report.
        <div className="canvas-debug-log">
          <div className="canvas-debug-log-header">
            <span>Debug-log</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={() => { debugLogRef.current = []; setDebugLog([]); }}>Wis</button>
              <button type="button" onClick={() => setDebugVisible(false)}>Verberg</button>
            </div>
          </div>
          {debugLog.length === 0
            ? <div className="muted">Nog niets gelogd — selecteer, sleep of resize een gebied.</div>
            : debugLog.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      ) : (
        <button
          type="button"
          className="canvas-debug-toggle"
          title="Debug-log tonen (voor het diagnosticeren van sleep/resize-problemen)"
          onClick={() => setDebugVisible(true)}
        >
          🐞
        </button>
      )}
    </div>
  );
}
